import { Component } from "@kluntje/core";
import { INVISIBLE, ACTIVE, VALID, INVALID, HIDDEN } from "Helper/cssClasses";
import { addClass, toggleClass, removeClass } from "@kluntje/js-utils/lib/dom-helpers";
import { html, render } from "lit-html";

class FRAFlightupdate extends Component {
  constructor() {
    super({
      props: {
        // i18n text shown to the user when the no email is provided
        emailInputMessage: { required: true, type: "string" },
        // i18n text shown to the user when the email input field isn't filled correct
        emailErrorMessage: { required: true, type: "string" },
        // i18n text shown to the user when they subscribe via email successfully
        emailSuccessMessage: { required: true, type: "string" },
        // ID of the input filed for the email address for the subscription
        emailInputId: { required: true, type: "string" },
      },

      ui: {
        showFormButton: ".fra-js-show__input :-one",
        input: ".fra-js-handle__input :-one",
        submit: ".fra-js-handle__submit :-one",
        submitIcons: ".fra-m-flightupdate__submit-icon",
        form: ".fra-m-flightupdate__form :-one",
        message: ".fra-m-flightupdate__form-message :-one",
        fbSendToMessengerCta: ".fra-js-flightupdate__fb-send-to-messenger-cta :-one",
        fbSendToMessengerOverlay: ".fra-m-overlay :-one",
        fbSentToMessenger: "fra-e-facebook-send-to-messenger",
      },

      events: [
        {
          event: "click",
          target: "showFormButton",
          handler: "showForm",
        },
        {
          event: "input",
          target: "input",
          handler: "handleInput",
        },
        {
          event: "click",
          target: "submit",
          handler: "handleSubmit",
        },
        {
          event: "click",
          target: "fbSendToMessengerCta",
          handler: "openFBSendToMessengerOverlay",
        },
        {
          event: "fra-e-facebook-send-to-messenger:opt-in",
          target: "fbSentToMessenger",
          handler: "handleFBMessengerOptIn",
        },
      ],
      initialStates: {
        isValid: true,
        isValidate: false,
      },
    });
  }

  /**
   * show the form when the user click on the email button
   */
  showForm(): void {
    addClass(this.ui.showFormButton, INVISIBLE);
  }

  /**
   * handle the input event on the input field
   */
  handleInput(): void {
    if (this.ui.input.value === "") {
      this.resetInput();
      this.clearInput();
    }
    if (this.state.isValidate) {
      this.resetInput();
    }
  }

  /**
   * Delete the value of the input field
   */
  clearInput(): void {
    this.ui.input.value = "";
  }

  /**
   * delete the feedback message from the input and reset the error or success classes
   */
  resetInput(): void {
    const form = this.ui.form as HTMLElement;
    const message = this.ui.message as HTMLSpanElement;

    form.classList.remove(INVALID);
    form.classList.remove(VALID);
    message.classList.remove(ACTIVE);

    render(html``, message);
    this.setState({ isValidate: false });

    this.ui.submitIcons.forEach((icon: HTMLElement) => {
      addClass(icon, INVISIBLE);
      if (icon.getAttribute("icon-id") === "fra-icon-arrow-right") {
        icon.classList.remove(INVISIBLE);
      }
    });
    this.clearInput();
  }

  /**
   * Handle the feedback behavior after a given user send the form
   * @param {string} stateClass - INVALID or VALID
   * @param {string} feedbackMessage with the feedback message you want show
   * @param {string} submitIcon - the icon id for the icon you want show on the submit
   */
  handleResponse(stateClass: string, feedbackMessage: string, submitIcon: string) {
    const { message, form } = this.ui;

    addClass(form, stateClass);
    addClass(message, ACTIVE);
    render(
      html`
        ${feedbackMessage}
      `,
      message,
    );
    this.setState({ isValidate: true });

    if (stateClass === VALID) {
      form.classList.remove(INVALID);
    }
    if (stateClass === INVALID) {
      form.classList.remove(VALID);
    }

    this.ui.submitIcons.forEach((icon: HTMLElement) => {
      toggleClass(icon, INVISIBLE, icon.getAttribute("icon-id") !== submitIcon);
    });
  }

  /**
   * on submit: if the input is valid send the form otherwise clear the form from feedback message
   *
   * @param {Event} event handler
   */
  handleSubmit(event: Event): void {
    this.ui.message.setAttribute("arialabeled-by", `${this.emailInputId}`);

    if (this.state.isValidate) {
      event.preventDefault();
      this.handleResponse(INVALID, this.emailErrorMessage, "fra-icon-close");
      this.resetInput();
      return;
    }

    const isInputEmpty = this.ui.input.value === "";
    if (isInputEmpty) {
      event.preventDefault();
      this.handleResponse(INVALID, this.emailInputMessage, "fra-icon-close");
      return;
    }

    if (this.ui.input.checkValidity()) {
      this.setState({ isValid: true });
      this.handleResponse(VALID, this.emailSuccessMessage, "fra-icon-check");
    } else {
      event.preventDefault();
      this.setState({ isValid: false });
      this.handleResponse(INVALID, this.emailErrorMessage, "fra-icon-close");
    }
  }

  /**
   * click event handler for the Facebook messenger CTA,
   * opens the overlay containing Facebook's send to messenger plugin,
   *
   * @param {UIEvent} _e - click event
   */
  openFBSendToMessengerOverlay(_e: UIEvent): void {
    addClass(this.ui.fbSendToMessengerCta, HIDDEN);
  }

  /**
   * when user has successfully subscribed to the messenger, close the overlay with the send to messenger plugin
   */
  handleFBMessengerOptIn() {
    // automatically close the send to messenger overlay after 2 seconds
    setTimeout(() => {
      removeClass(this.ui.fbSendToMessengerCta, HIDDEN);
    }, 2000);
  }
}

customElements.define("fra-m-flightupdate", FRAFlightupdate);

export default FRAFlightupdate;
